import React from "react";
import { Box, Container, Typography, Grid } from "@mui/material";
import { motion } from "framer-motion";

// Import Assets
import img1 from "../../Assets/InteriorDesign/Approcahsection/Approch1.png";
import img2 from "../../Assets/InteriorDesign/Approcahsection/Approch2.png";
import img3 from "../../Assets/InteriorDesign/Approcahsection/Approch3.png";
import img4 from "../../Assets/InteriorDesign/Approcahsection/Approach4.png";

import icon1 from "../../Assets/InteriorDesign/Approcahsection/Icons/Icon.svg";
import icon2 from "../../Assets/InteriorDesign/Approcahsection/Icons/Icon2.svg";
import icon3 from "../../Assets/InteriorDesign/Approcahsection/Icons/Icon3.svg";
import icon4 from "../../Assets/InteriorDesign/Approcahsection/Icons/Icon4.svg";
import icon5 from "../../Assets/InteriorDesign/Approcahsection/Icons/Icon5.svg";

const ApproachSection = () => {
  const features = [
    {
      icon: icon1,
      title: "Spatial Analysis",
      desc: "The foundation of every project begins with a deep forensic audit of flow, light, and volume.",
    },
    {
      icon: icon2,
      title: "Material Curation",
      desc: "Sourcing rare textiles, hand-finished stones, and custom millwork that defines exclusivity.",
    },
    {
      icon: icon3,
      title: "Turnkey Management",
      desc: "A seamless white-glove execution from initial conceptual sketch to final installation.",
    },
    {
      icon: icon4,
      title: "Precision Measurements",
      desc: "Meticulous on-site measurements ensure every element fits seamlessly within the spatial framework.",
    },
    {
      icon: icon5,
      title: "Lighting Strategy",
      desc: "A considered layering of natural and artificial light to enhance mood, depth, and spatial perception.",
    },
  ];

  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: "#FFFFFF",
        overflow: "hidden",
      }}
    >
      <Container maxWidth={false} sx={{ maxWidth: "1280px" }}>
        <Grid container spacing={{ xs: 6, md: 8 }} alignItems="flex-start">
          {/* Left Content Column */}
          <Grid item xs={12} md={5}>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <Box
                sx={{
                  width: { md: "469.33px" },
                  height: { md: "96px" },
                  mb: 4,
                }}
              >
                <Typography
                  variant="h2"
                  sx={{
                    fontFamily: "Manrope",
                    fontWeight: 700,
                    fontSize: { xs: "32px", md: "48px" },
                    lineHeight: { xs: "1.1", md: "48px" },
                    color: "#1C1917",
                    letterSpacing: "-1.2px",
                    textAlign: "left",
                  }}
                >
                  Our Strategic <br />
                  Alchemy
                </Typography>
              </Box>

              <Box
                sx={{
                  width: { md: "469.33px" },
                  height: { md: "88px" },
                  mb: 6,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontSize: "18px",
                    lineHeight: "29.25px",
                    color: "#5A413A",
                    fontWeight: 400,
                    textAlign: "left",
                  }}
                >
                  We don't just furnish rooms; we engineer experiences. Our
                  approach is a rigorous dialogue between structural integrity
                  and sensory detail.
                </Typography>
              </Box>

              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "48px" }}
              >
                {features.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: "20px",
                        width: { md: "469.33px" },
                        height: { md: "81.5px" },
                      }}
                    >
                      <Box
                        sx={{
                          width: "54px",
                          height: "53px",
                          minWidth: "54px",
                          backgroundColor: "#EBE7E7",
                          borderRadius: "8px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <img
                          src={feature.icon}
                          alt={feature.title}
                          style={{ width: "11px", height: "18px" }}
                        />
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontFamily: "Manrope",
                            fontWeight: 700,
                            fontSize: "18px",
                            color: "#1C1917",
                            mb: 0.5,
                            textAlign: "left",
                          }}
                        >
                          {feature.title}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "Inter",
                            fontSize: "14px",
                            color: "#78716C",
                            lineHeight: "20px",
                            textAlign: "left",
                          }}
                        >
                          {feature.desc}
                        </Typography>
                      </Box>
                    </Box>
                  </motion.div>
                ))}
              </Box>
            </motion.div>
          </Grid>

          {/* Right Image Grid Column */}
          <Grid item xs={12} md={7}>
            <Box
              sx={{
                position: "relative",
                width: { xs: "100%", md: "682.66px" },
                height: { xs: "auto", md: "841.76px" },
                display: "flex",
                justifyContent: "flex-start",
                gap: "16px",
              }}
            >
              {/* Left Column of Images */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                  mt: { md: 6 },
                  width: { xs: "50%", md: "auto" },
                }}
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                >
                  <Box
                    component="img"
                    src={img1}
                    sx={{
                      width: "100%",
                      maxWidth: "333.33px",
                      height: { xs: "200px", sm: "300px", md: "444.44px" },
                      borderRadius: "8px",
                      objectFit: "cover",
                      boxShadow: "2px 2px 10px 0px #00000040",
                    }}
                  />
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                >
                  <Box
                    component="img"
                    src={img2}
                    sx={{
                      width: "100%",
                      maxWidth: "333.33px",
                      height: { xs: "200px", sm: "300px", md: "333.33px" },
                      borderRadius: "8px",
                      objectFit: "cover",
                      boxShadow: "2px 2px 10px 0px #00000040",
                    }}
                  />
                </motion.div>
              </Box>

              {/* Right Column of Images */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                  width: { xs: "50%", md: "auto" },
                }}
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.1 }}
                >
                  <Box
                    component="img"
                    src={img3}
                    sx={{
                      width: "100%",
                      maxWidth: "333.33px",
                      height: { xs: "150px", sm: "220px", md: "333.33px" },
                      borderRadius: "8px",
                      objectFit: "cover",
                      boxShadow: "2px 2px 10px 0px #00000040",
                    }}
                  />
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                >
                  <Box
                    component="img"
                    src={img4}
                    sx={{
                      width: "100%",
                      maxWidth: "333.33px",
                      height: { xs: "200px", sm: "300px", md: "444.44px" },
                      borderRadius: "8px",
                      objectFit: "cover",
                      boxShadow: "2px 2px 10px 0px #00000040",
                    }}
                  />
                </motion.div>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ApproachSection;
